import React, { useMemo, useState } from 'react';
import { Play, ExternalLink } from 'lucide-react';
import { TwitchIcon, YouTubeIcon, InstagramIcon } from '../icons/SocialIcons';
import {
  INSTAGRAM_URL,
  IMAGE_INSTAGRAM_PREVIEW,
  IMAGE_TWITCH_PREVIEW,
  TWITCH_URL,
  YOUTUBE_ASMR_URL,
  YOUTUBE_CERAMICA_URL,
  YOUTUBE_VIDEO_ASMR_ID,
  YOUTUBE_VIDEO_CERAMICA_ID,
  youtubeEmbed,
  youtubeThumbnail,
} from '../../constants/social';

interface VideoCard {
  id: string;
  platform: 'youtube' | 'twitch' | 'instagram';
  title: string;
  channel: string;
  channelUrl: string;
  embedUrl: string;
  thumbnail: string;
  accent: string;
  border: string;
  badge: string;
  icon: React.ReactNode;
  externalOnly?: boolean;
}

export default function VideoShowcase() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const videos: VideoCard[] = useMemo(() => [
    {
      id: 'ceramica',
      platform: 'youtube',
      title: 'ASMR con Cerámica',
      channel: '@CeramiCalmaASMR',
      channelUrl: YOUTUBE_CERAMICA_URL,
      embedUrl: youtubeEmbed(YOUTUBE_VIDEO_CERAMICA_ID),
      thumbnail: youtubeThumbnail(YOUTUBE_VIDEO_CERAMICA_ID),
      accent: 'from-rose-400/15 to-pink-400/5',
      border: 'border-rose-400/25 hover:border-rose-400/50',
      badge: 'bg-rose-400/15 text-rose-300',
      icon: <YouTubeIcon className="w-4 h-4" />,
    },
    {
      id: 'eugenial-asmr',
      platform: 'youtube',
      title: 'EuGe_NiAL ASMR',
      channel: '@EugenialASMR',
      channelUrl: YOUTUBE_ASMR_URL,
      embedUrl: youtubeEmbed(YOUTUBE_VIDEO_ASMR_ID),
      thumbnail: youtubeThumbnail(YOUTUBE_VIDEO_ASMR_ID),
      accent: 'from-sky-400/15 to-blue-400/5',
      border: 'border-sky-400/25 hover:border-sky-400/50',
      badge: 'bg-sky-400/15 text-sky-300',
      icon: <YouTubeIcon className="w-4 h-4" />,
    },
    {
      id: 'twitch',
      platform: 'twitch',
      title: 'Directo en vivo — Twitch',
      channel: 'EuGe_NiAL',
      channelUrl: TWITCH_URL,
      embedUrl: '',
      thumbnail: IMAGE_TWITCH_PREVIEW,
      accent: 'from-violet-400/15 to-purple-400/5',
      border: 'border-violet-400/25 hover:border-violet-400/50',
      badge: 'bg-violet-400/15 text-violet-300',
      icon: <TwitchIcon className="w-4 h-4" />,
      externalOnly: true,
    },
    {
      id: 'instagram',
      platform: 'instagram',
      title: 'Clips y momentos — Instagram',
      channel: '@euge_nial_',
      channelUrl: INSTAGRAM_URL,
      embedUrl: '',
      thumbnail: IMAGE_INSTAGRAM_PREVIEW,
      accent: 'from-fuchsia-400/15 to-pink-400/5',
      border: 'border-fuchsia-400/25 hover:border-fuchsia-400/50',
      badge: 'bg-fuchsia-400/15 text-fuchsia-300',
      icon: <InstagramIcon className="w-4 h-4" />,
      externalOnly: true,
    },
  ], []);

  return (
    <section className="relative bg-gradient-to-b from-[#0a0815] via-[#0d0a1a] to-[#0a0815] py-24 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-rose-300/60 text-sm font-medium tracking-[0.2em] uppercase mb-3">Contenido</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-white/90">Mis trabajos y redes</h2>
          <p className="mt-4 text-slate-400 text-base max-w-xl mx-auto">
            Videos, directos y perfil. Reproducí acá o visitá el canal directo.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {videos.map((v) => {
            const isActive = activeVideo === v.id;
            const canPlay = !!v.embedUrl && !v.externalOnly;

            return (
              <div
                key={v.id}
                className={`group flex flex-col rounded-2xl border bg-gradient-to-br ${v.accent} ${v.border} overflow-hidden transition-all duration-300`}
              >
                <div className="relative aspect-video bg-black/40">
                  {isActive && canPlay ? (
                    <iframe
                      src={v.embedUrl}
                      title={v.title}
                      className="absolute inset-0 w-full h-full"
                      allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <img
                        src={v.thumbnail}
                        alt={v.title}
                        className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-90 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      {canPlay ? (
                        <button
                          type="button"
                          onClick={() => setActiveVideo(isActive ? null : v.id)}
                          className="absolute inset-0 flex items-center justify-center"
                          aria-label="Reproducir video"
                        >
                          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 group-hover:scale-110 group-hover:bg-white/25 transition-all duration-300">
                            <Play className="w-7 h-7 text-white fill-white ml-1" />
                          </span>
                        </button>
                      ) : (
                        <a
                          href={v.channelUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center group/link"
                          aria-label={v.platform === 'twitch' ? 'Abrir canal de Twitch' : 'Abrir perfil de Instagram'}
                        >
                          <span className="px-4 py-2 rounded-full bg-black/40 backdrop-blur-sm text-white/80 text-sm opacity-0 group-hover/link:opacity-100 transition-opacity">
                            {v.platform === 'twitch' ? 'Ver canal en Twitch' : 'Ver perfil en Instagram'}
                          </span>
                        </a>
                      )}
                      <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${v.badge} backdrop-blur-sm`}>
                        {v.icon}
                        {v.platform === 'youtube' ? 'YouTube' : v.platform === 'twitch' ? 'Twitch' : 'Instagram'}
                      </span>
                    </>
                  )}
                </div>

                <div className="p-5 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-white/90 font-semibold text-sm mb-1 truncate">{v.title}</h3>
                    <p className="text-slate-400 text-xs truncate">{v.channel}</p>
                  </div>
                  <a
                    href={v.channelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white text-xs font-medium transition-all"
                  >
                    Visitar
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
